import axios, { type AxiosInstance } from 'axios';
import { apiClient } from '@/lib/api-client';
import { jwtClaims } from '@/lib/jwt';
import { tokenStore } from '@/lib/token-store';
import { keyFor } from './keys';
import type { ClassifyInput, OutboxItem, OutboxRequest, OutboxResponse } from './types';

/** A request that went out and got anything but a 2xx answer (or no answer). The engine classifies `res`. */
export class OutboxSendError extends Error {
  constructor(readonly res: ClassifyInput) {
    super(res.message ?? (res.network ? 'Network error' : `HTTP ${res.status}`));
    this.name = 'OutboxSendError';
  }
}

/** The request was not sent: the signed-in session is not the item's. The item waits for its own user. */
export class WrongUserError extends Error {
  constructor() {
    super('The signed-in session is not the outbox item\'s user');
    this.name = 'WrongUserError';
  }
}

export interface Transport {
  send(item: OutboxItem, req: OutboxRequest): Promise<OutboxResponse>;
}

type Body = Record<string, unknown>;
const isObject = (v: unknown): v is Body => typeof v === 'object' && v !== null && !Array.isArray(v);
const isBytes = (v: unknown) => v instanceof Blob || v instanceof ArrayBuffer || ArrayBuffer.isView(v);
const text = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

/** The interceptor's refresh calls. Only a refresh the server refused (4xx) means the session is gone. */
const REFRESH_CALL = /\/(auth|mobile\/sessions)\/refresh$/;
/** The interceptor's own refresh errors: no token to refresh with, or an answer without tokens. */
const NO_SESSION = /no refresh token|carried no tokens/i;

/**
 * The outbox's HTTP. Every request goes through `apiClient.instance`, so a 401 first runs the normal refresh and the request is
 * retried once with the same headers (the same key). A 401 that still comes back, or a refresh the server refused, means sign-in
 * is needed; a refresh lost on the network is retried.
 */
export function createHttpTransport(client: () => AxiosInstance = () => apiClient.instance): Transport {
  return {
    async send(item, req) {
      // the type already requires `event` on a write; checked at run time too (a cast or plain JS), because without it the
      // server derives client_event_id from the key
      const unchecked = req as { method: string; path: string; event?: unknown };
      if (unchecked.method !== 'GET' && typeof unchecked.event !== 'boolean') {
        throw new TypeError(`${unchecked.method} ${unchecked.path}: a write must say whether it is a device event (event: true | false)`);
      }
      assertSessionIsFor(item);
      const headers: Record<string, string> = { 'X-Client-Sent-At': new Date().toISOString() };
      // jobs B9 requires a key on every write; a GET is never replayed from the key store (a status read must be live)
      if (req.method !== 'GET') headers['Idempotency-Key'] = keyFor(item, req.step, req.index);

      let data = req.method === 'GET' ? undefined : req.body;
      if (isBytes(data)) {
        headers['Content-Type'] = 'application/octet-stream';
      } else if (data instanceof FormData) {
        // the instance defaults to JSON, which would make axios turn the form into JSON; the browser adds the boundary
        headers['Content-Type'] = 'multipart/form-data';
        if (req.event) {
          data.set('client_time', item.clientTime);
          data.set('client_event_id', item.id);
        }
      } else if (req.event) {
        // set last, so a payload can never carry another client_event_id
        data = { ...(isObject(data) ? data : {}), client_time: item.clientTime, client_event_id: item.id };
      }

      try {
        const res = await client().request({
          method: req.method,
          url: req.path,
          data,
          headers,
          // raw bytes go out untouched, whatever axios's runtime sniffing makes of the Blob
          ...(isBytes(data) && { transformRequest: [(bytes: unknown) => bytes] }),
        });
        return answerOf(res.status, res.data);
      } catch (e) {
        throw new OutboxSendError(failureOf(e));
      }
    },
  };
}

/** Never send an item with another user's token: when the access token names a user or organization, it must be the item's. */
function assertSessionIsFor(item: OutboxItem): void {
  const claims = jwtClaims(tokenStore.getAccess());
  if (!claims) return; // no access token yet: the interceptor refreshes this session's own token
  const user = claims.user_id;
  const org = claims.organization_id;
  if ((user !== undefined && Number(user) !== item.userId) || (org !== undefined && Number(org) !== item.orgId)) {
    throw new WrongUserError();
  }
}

function answerOf(status: number, body: unknown): OutboxResponse {
  const envelope = isObject(body) ? body : {};
  const data = 'success' in envelope && 'data' in envelope ? envelope.data : body;
  const inner = isObject(data) ? data : {};
  const applied = inner.applied ?? envelope.applied;
  const flag = inner.flag ?? envelope.flag;
  const errors = isObject(envelope.errors) ? envelope.errors : {};
  const error = isObject(envelope.error) ? envelope.error : {};
  return {
    status,
    data,
    applied: typeof applied === 'boolean' ? applied : undefined,
    flag: typeof flag === 'string' ? flag : flag === null ? null : undefined,
    code: text(envelope.code) ?? text(errors.type) ?? text(errors.code) ?? text(error.code) ?? text(envelope.type),
    message: text(envelope.message) ?? text(errors.message) ?? text(error.message) ?? text(envelope.error),
  };
}

function failureOf(e: unknown): ClassifyInput {
  if (axios.isAxiosError(e)) {
    if (!e.response) return { network: true, message: e.message }; // a refresh lost on the network included: retry, never hold
    const { data: _data, ...answer } = answerOf(e.response.status, e.response.data);
    const status = e.response.status;
    // a refresh refused by the server (401 invalid_refresh_token / refresh_reused, or any 4xx but 429) needs a sign-in; its 5xx
    // and 429 are retried like any other answer
    if (REFRESH_CALL.test(e.config?.url ?? '') && status >= 400 && status < 500 && status !== 429) {
      return { ...answer, status: 401, code: answer.code ?? 'refresh_failed' };
    }
    return answer;
  }
  const message = e instanceof Error ? e.message : String(e);
  if (NO_SESSION.test(message)) return { status: 401, code: 'refresh_failed', message };
  return { network: true, message }; // anything else unexpected is retried with backoff (and expires after 7 days)
}
